import { ChangeEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch } from "./app/hooks";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { Localized, useLocalization } from "@fluent/react";
import { showError } from "./features/notifications/notificationsSlice";
import { AuthPageLayout } from "./AuthPageLayout";
import { LanguageSelector } from "./LanguageSelector";
import { APIError } from "./app/appTypes";
import { register } from './features/user/userSlice';
import { RegistrationAwaitApprovalPanel } from "./RegistrationAwaitApprovalPanel";

const PREFIX = 'Register';

const classes = {
    paper: `${PREFIX}-paper`,
    logo: `${PREFIX}-logo`,
    text: `${PREFIX}-text`,
    backdrop: `${PREFIX}-backdrop`
};

type ViewState = 'registration' | 'awaiting-approval'

export const Register = () => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [state, setState] = useState<ViewState>('registration')

    const [email, setEmail] = useState("");
    const [emailConfirmation, setEmailConfirmation] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");

    const [emailError, setEmailError] = useState("");
    const [emailConfirmationError, setEmailConfirmationError] = useState("");
    const [companyError, setCompanyError] = useState("");
    const [mask, setMask] = useState<boolean>(false);

    const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
    const onEmailConfirmChange = (e: ChangeEvent<HTMLInputElement>) => setEmailConfirmation(e.target.value);
    const onCompanyChange = (e: ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value);
    const onPhoneChange = (e: ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateEmail = () => {
        setEmailError(emailRegex.test(email) ? "" : "email-incorrect");
    };

    const validateEmailConfirmation = () => {
        setEmailConfirmationError(email === emailConfirmation ? "" : "emails-do-not-match");
    };

    const validateCompany = () => {
        setCompanyError(companyName.trim() ? "" : "field-is-required");
    };

    const isFormValid = () => {
        const emailValid = emailRegex.test(email);
        const matchValid = email === emailConfirmation;
        const companyValid = companyName.trim().length > 0;

        setEmailError(emailValid ? "" : "Nieprawidłowy adres e-mail");
        setEmailConfirmationError(matchValid ? "" : "Adresy e-mail się nie zgadzają");
        setCompanyError(companyValid ? "" : "Nazwa firmy jest wymagana");

        return emailValid && matchValid && companyValid;
    };

    const canSubmit = email && emailConfirmation && companyName && !emailError && !emailConfirmationError && !companyError;

    const handleConfirmRegistration = async () => {
        if (!isFormValid()) return;

        setMask(true)


        try {
            await dispatch(register({ email, emailConfirmation, companyName, phoneNumber })).unwrap()
            setState('awaiting-approval')
        }
        catch (error) {
            const payload = error as APIError
            if (payload) {
                switch (payload.kind) {
                    case "http":
                        if (payload.body) {
                            const apiResponse = JSON.parse(payload.body)
                            if (apiResponse.title) {
                                dispatch(showError(apiResponse.title ?? "error-registration-failed"))
                            }
                            else {
                                dispatch(showError("error-registration-failed"))
                            }

                        } else {
                            dispatch(showError("error-registration-failed"))
                        }
                        break
                    case "connection":
                        dispatch(showError("connection-issue"))
                        break
                }
            } else {
                dispatch(showError("error-registration-failed"))
            }
            setMask(false)
        }
        finally {
            setMask(false)
        }
    }


    const handleEnter = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleConfirmRegistration();
    };

    let content = <></>
    switch (state) {

        case 'registration':
            content =
                < AuthPageLayout>
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <Localized id="registration">
                                    <span>Rejestracja</span>
                                </Localized>
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                className={classes.text}
                                value={email}
                                fullWidth
                                required
                                onBlur={validateEmail}
                                error={!!emailError}
                                helperText={!!emailError ? l10n.getString(emailError, null, "error") : ""}
                                type="email"
                                onChange={onEmailChange}
                                inputProps={{ maxLength: 100, autoComplete: "email" }}
                                label={<Localized id="user-email">_Email</Localized>}
                                onKeyDown={handleEnter}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                className={classes.text}
                                value={emailConfirmation}
                                required
                                fullWidth
                                onBlur={validateEmailConfirmation}
                                error={!!emailConfirmationError}
                                helperText={!!emailConfirmationError ? l10n.getString(emailConfirmationError, null, "error") : ""}
                                type="email"
                                onChange={onEmailConfirmChange}
                                inputProps={{ maxLength: 100, autoComplete: "email-confirm" }}
                                label={<Localized id="user-email-confirm">_Powtórz e-mail</Localized>}
                                onKeyDown={handleEnter}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box component="form" autoComplete="off">
                                <TextField
                                    className={classes.text}
                                    value={companyName}
                                    required
                                    fullWidth
                                    onBlur={validateCompany}
                                    error={!!companyError}
                                    helperText={!!companyError ? l10n.getString(companyError, null, "error") : ""}
                                    onChange={onCompanyChange}
                                    label={<Localized id="companies-name">_Nazwa firmy</Localized>}
                                    onKeyDown={handleEnter}
                                    inputProps={{ maxLength: 100, autoComplete: "off" }}
                                    name="company-no-autofill"
                                    autoComplete="off"
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                className={classes.text}
                                value={phoneNumber}
                                onChange={onPhoneChange}
                                type="tel"
                                fullWidth
                                inputMode="tel"
                                inputProps={{ maxLength: 30, autoComplete: "tel" }}
                                label={<Localized id="companies-phone">_Telefon (opcjonalnie)</Localized>}
                                onKeyDown={handleEnter}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                onClick={handleConfirmRegistration}
                                color="primary"
                                size="large"
                                variant="contained"
                                disabled={!canSubmit || mask}
                                fullWidth
                            >
                                <Localized id="register">
                                    <span>Zarejestruj</span>
                                </Localized>
                            </Button>
                        </Grid>
                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20, marginBottom: 10 }} />

                        <Grid item xs={12}
                            textAlign="center"
                            sx={{
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                py: 1.5,
                                marginLeft: 2
                            }}
                        >
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{ whiteSpace: 'nowrap', fontWeight: '800', display: 'inline-flex', alignItems: 'center' }}
                            >
                                <Localized id="have-account">
                                    <span>Masz już konto?</span>
                                </Localized>{" "}
                                <Button
                                    component={Link}
                                    to="/login"
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    sx={{
                                        ml: 2,
                                        px: 1.25,
                                        py: 0.25,
                                        minWidth: 'auto',
                                        textTransform: 'none',
                                        display: 'inline-flex',
                                    }}
                                >
                                    <Localized id="login">
                                        <span>Zaloguj się</span>
                                    </Localized>
                                </Button>
                            </Typography>
                        </Grid>
                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20, marginBottom: 10 }} />

                        <Grid item textAlign="center">
                            <LanguageSelector />
                        </Grid>
                    </Grid>

                    <Backdrop className={classes.backdrop} open={mask}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </ AuthPageLayout >

            break
        case 'awaiting-approval':
            content =
                <Container sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: "#f4f6f8",
                    height: '100vh',
                }} component={Paper} maxWidth={false} >
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <RegistrationAwaitApprovalPanel />
                    </Box>

                </Container>
            break
    }

    return (
        <>
            {content}
        </>

    );
};
